import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Camera, Send, Sparkles, X, ArrowLeft, Leaf, MapPin } from 'lucide-react';
import { aiApi, plantsApi, zonesApi } from '../services/gardenApi';
import { convertHeicToJpeg, isHeicFile } from '../../utils/convertHeic';
import { useGardenPageTitle } from '../useGardenPageTitle';

// Suggested prompts shown before the user sends their first message. Kept
// short + general so they work without any garden context.
const DEFAULT_SUGGESTIONS = [
  "What's wrong with this plant? (add a photo)",
  "What should I do in my garden this week?",
  "Which of my plants need pruning soon?",
  "Which plants attract pollinators?",
];

// `intent` query param maps to these (optional server tuning later).
const INTENT_PROMPTS = {
  placement:
    "I'm at a garden centre looking at a plant—I'll add a photo of the plant or label. Which of my garden zones is the best fit, and why? Use each zone's sun and site notes. If you can't tell the species from the photo, ask me for the name on the tag.",
  companion:
    'Suggest companion plants that grow well with the plant I mean (from my photo, context, or name in this message). Use my real zones, sun, site notes, and what I already grow where it helps.',
  care:
    "Give practical care instructions for this plant—I'll add a photo or name below. What matters this season, watering, and anything I should do soon?",
  diagnose:
    "Plant doctor: I'll add a photo of damaged or spotted leaves. What might be going on, what should I watch for, and what are sensible first steps? Say clearly if the photo isn't enough to tell.",
  weed:
    "Is this a weed? I'll add a photo. Say whether it's likely a weed or a desirable garden plant for a home yard, and what you'd do next.",
};

/** Empty-state quick actions: label + prompt (or intent key into INTENT_PROMPTS). */
const QUICK_ACTIONS = [
  { id: 'placement', label: 'Where to plant (shop)', prompt: INTENT_PROMPTS.placement },
  { id: 'companion', label: 'Good neighbours', prompt: INTENT_PROMPTS.companion },
  { id: 'care', label: 'Care help', prompt: INTENT_PROMPTS.care },
  { id: 'diagnose', label: 'Plant doctor', prompt: INTENT_PROMPTS.diagnose },
  { id: 'weed', label: 'Weed check (photo)', prompt: INTENT_PROMPTS.weed },
];

function stripUrlStarters(setSearch, currentSearch) {
  const next = new URLSearchParams(currentSearch);
  next.delete('starter');
  next.delete('intent');
  next.delete('identified_common');
  next.delete('identified_scientific');
  setSearch(next, { replace: true });
}

function PlantContextChip({ plant, onClear }) {
  return (
    <div className="mx-3 mb-2 inline-flex items-center gap-1.5 px-3 min-h-8 rounded-full bg-garden-primary/10 text-garden-primary text-xs font-medium">
      <Leaf size={12} />
      <span className="truncate max-w-[200px]">About: {plant.common_name}</span>
      {onClear ? (
        <button onClick={onClear} aria-label="Clear context" className="opacity-70 ml-1">
          <X size={12} />
        </button>
      ) : null}
    </div>
  );
}

function ZoneContextChip({ zone, onClear }) {
  return (
    <div className="mx-3 mb-2 inline-flex items-center gap-1.5 px-3 min-h-8 rounded-full bg-garden-primary/10 text-garden-primary text-xs font-medium">
      <MapPin size={12} />
      <span className="truncate max-w-[200px]">About: {zone.name}</span>
      {onClear ? (
        <button onClick={onClear} aria-label="Clear context" className="opacity-70 ml-1">
          <X size={12} />
        </button>
      ) : null}
    </div>
  );
}

export default function AIHelpScreen() {
  useGardenPageTitle('AI Help', 'AI Help');
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const plantId = search.get('plant_id') || '';
  const zoneId  = search.get('zone_id')  || '';
  const starterQ = search.get('starter') || '';
  const intentQ = search.get('intent') || '';
  const identifiedCommon = search.get('identified_common') || '';
  const identifiedScientific = search.get('identified_scientific') || '';

  const [plant, setPlant] = useState(null);
  const [zone, setZone]   = useState(null);

  const [messages, setMessages] = useState([]); // {role, text, imageUrl?}[]
  const [input, setInput] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState(null);
  /** Shown after URL prefill so mobile users know to scroll to the composer. */
  const [composerHint, setComposerHint] = useState(null);

  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const scrollRef = useRef(null);

  // Load persisted chat history once on mount so returning users see their
  // prior conversation instead of a cold welcome card.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await aiApi.chatHistory();
        if (!cancelled) {
          const shaped = (r.messages || []).map((m) => ({
            role: m.role,
            text: m.text,
            imageUrl: m.image_url || null,
          }));
          setMessages(shaped);
        }
      } catch {
        // Silent: empty history is a fine starting state.
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Preload focus plant / zone names for the context chip, if provided.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (plantId) {
        try {
          const r = await plantsApi.get(plantId);
          if (!cancelled) setPlant(r.plant);
        } catch { /* non-fatal; chip just won't render */ }
      } else setPlant(null);
      if (zoneId) {
        try {
          const r = await zonesApi.get(zoneId);
          if (!cancelled) setZone(r.zone);
        } catch { /* non-fatal */ }
      } else setZone(null);
    })();
    return () => { cancelled = true; };
  }, [plantId, zoneId]);

  // Deep link: ?starter=identified&… or ?intent=… pre-fills the composer (even if chat history exists).
  useEffect(() => {
    if (historyLoading) return;

    let text = '';
    if (starterQ === 'identified' && (identifiedCommon || identifiedScientific)) {
      const label = [identifiedCommon, identifiedScientific].filter(Boolean).join(' — ') || 'this plant';
      text =
        `I used PlantNet at the garden centre and the best match looks like: ${label}. Which of my garden zones should it go in—using my sun and site notes for each zone—and what companion plants would work well with it in my yard?`;
    } else if (intentQ && INTENT_PROMPTS[intentQ]) {
      text = INTENT_PROMPTS[intentQ];
    }

    if (text) {
      setInput(text);
      stripUrlStarters(setSearch, search);
      setComposerHint(
        'Your message is in the box below—you can edit it before sending. Scroll down if you do not see it.',
      );
    }
  }, [
    historyLoading,
    starterQ,
    intentQ,
    identifiedCommon,
    identifiedScientific,
    setSearch,
    search,
  ]);

  // After URL prefill, bring the composer into view on mobile (keyboard-safe scroll).
  useEffect(() => {
    if (!composerHint) return undefined;
    const id = window.requestAnimationFrame(() => {
      textareaRef.current?.focus({ preventScroll: false });
      textareaRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [composerHint]);

  // Create/revoke object URL for the selected image.
  useEffect(() => {
    if (!image) { setImagePreview(null); return undefined; }
    const url = URL.createObjectURL(image);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  // Auto-scroll to latest message.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const canSend = !sending && (input.trim().length > 0 || image);

  const send = useCallback(async () => {
    if (!canSend) return;
    const msgText = input.trim();
    const userMessage = {
      role: 'user',
      text: msgText || '(photo)',
      imageUrl: imagePreview || null,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setComposerHint(null);
    setError(null);
    const currentImage = image;
    setImage(null);
    setSending(true);

    try {
      const r = await aiApi.chat({
        message: msgText,
        image: currentImage,
        plant_id: plantId || undefined,
        zone_id:  zoneId  || undefined,
      });
      // Server is source of truth for persisted turns; replace our optimistic
      // user bubble with the canonical pair it just saved so refreshes match.
      setMessages((prev) => {
        const next = prev.slice(0, -1);
        if (r.user_message) {
          next.push({
            role: 'user',
            text: r.user_message.text,
            imageUrl: r.user_message.image_url || userMessage.imageUrl,
          });
        } else {
          next.push(userMessage);
        }
        next.push({
          role: 'model',
          text: (r.model_message && r.model_message.text) || r.reply || '(no reply)',
        });
        return next;
      });
    } catch (e) {
      let text;
      switch (e.code) {
        case 'rate_limited':   text = 'AI is busy right now. Try again in a few seconds.'; break;
        case 'daily_limit':    text = e.message || 'Daily AI limit reached.'; break;
        case 'not_configured': text = 'AI chat is not configured on the server.'; break;
        case 'ai_unavailable': text = 'AI is temporarily unavailable.'; break;
        default:               text = e.message || 'Chat failed. Try again.';
      }
      setError(text);
      // Roll back the optimistic user bubble on failure so the input can be
      // retried cleanly.
      setMessages((prev) => prev.slice(0, -1));
      setInput(msgText);
      if (currentImage) setImage(currentImage);
    } finally {
      setSending(false);
    }
  }, [canSend, input, image, imagePreview, plantId, zoneId]);

  const clearChat = useCallback(async () => {
    if (!window.confirm('Clear this conversation? This cannot be undone.')) return;
    try {
      await aiApi.clearChatHistory();
      setMessages([]);
      setComposerHint(null);
      setError(null);
    } catch (e) {
      setError(e.message || 'Could not clear conversation');
    }
  }, []);

  const onSuggest = (text) => {
    setInput(text);
  };

  const onQuickAction = (prompt) => {
    setInput(prompt);
  };

  const clearContext = () => {
    const next = new URLSearchParams(search);
    next.delete('plant_id');
    next.delete('zone_id');
    next.delete('starter');
    next.delete('intent');
    next.delete('identified_common');
    next.delete('identified_scientific');
    setSearch(next, { replace: true });
  };

  const onPickImage = async (e) => {
    let f = e.target.files?.[0];
    if (f) {
      if (isHeicFile(f)) {
        try {
          f = await convertHeicToJpeg(f);
        } catch (err) {
          alert(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
          e.target.value = '';
          return;
        }
      }
      setImage(f);
    }
    e.target.value = '';
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <header className="flex items-center gap-2 px-2 pt-2 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-ink"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <div className="flex-1 flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-garden-primary/10 text-garden-primary flex items-center justify-center">
            <Sparkles size={18} />
          </div>
          <div>
            <h1 className="text-base font-semibold text-garden-ink leading-tight">AI Help</h1>
            <p className="text-[11px] text-garden-muted leading-tight">
              Knows your plants and zones
            </p>
          </div>
        </div>
        {messages.length > 0 ? (
          <button
            onClick={clearChat}
            className="text-xs text-garden-muted px-3 min-h-9"
          >
            Clear
          </button>
        ) : null}
      </header>

      {plant ? <PlantContextChip plant={plant} onClear={clearContext} /> : null}
      {zone  ? <ZoneContextChip  zone={zone}  onClear={clearContext} /> : null}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 pb-3">
        {historyLoading ? (
          <div className="px-1 pt-6 text-sm text-garden-muted">Loading conversation…</div>
        ) : messages.length === 0 ? (
          <div className="px-1 pt-2">
            <div className="rounded-2xl bg-white border border-garden-ink/10 p-4 text-sm text-garden-ink">
              Hey! I can help with plant IDs from photos, problem diagnosis,
              timing of garden chores, and advice tailored to your own plants
              and zones. What would you like to know?
            </div>
            <div className="mt-4">
              <p className="text-[11px] font-medium uppercase tracking-wide text-garden-muted mb-2">
                Quick actions
              </p>
              <div className="flex flex-wrap gap-2">
                {QUICK_ACTIONS.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => onQuickAction(a.prompt)}
                    className="px-3 min-h-9 rounded-xl bg-garden-primary/10 border border-garden-primary/25 text-xs font-medium text-garden-primaryDark hover:bg-garden-primary/15 text-left max-w-full"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3 rounded-xl border border-dashed border-garden-ink/15 px-3 py-2.5">
              <p className="text-[11px] text-garden-muted">Want a human eye on your plan?</p>
              <button
                type="button"
                disabled
                className="mt-1 text-xs font-semibold text-garden-muted/60 cursor-not-allowed"
              >
                Ask a designer (coming soon)
              </button>
            </div>
            <p className="mt-4 text-[11px] font-medium uppercase tracking-wide text-garden-muted">
              More ideas
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {DEFAULT_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onSuggest(s)}
                  className="px-3 min-h-8 rounded-full bg-white border border-garden-ink/10 text-xs text-garden-ink hover:border-garden-primary/40"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <ul className="space-y-2 pt-1">
            {messages.map((m, i) => <MessageBubble key={i} message={m} />)}
            {sending ? (
              <li className="flex">
                <div className="max-w-[85%] rounded-2xl bg-white border border-garden-ink/10 px-3 py-2 text-sm text-garden-muted italic">
                  Thinking{'\u2026'}
                </div>
              </li>
            ) : null}
          </ul>
        )}
      </div>

      {error ? (
        <div className="mx-3 mb-2 p-2 rounded-xl bg-red-50 text-xs text-red-700">
          {error}
        </div>
      ) : null}

      {composerHint ? (
        <div className="mx-3 mb-2 flex items-start gap-2 rounded-xl border border-garden-primary/25 bg-garden-primaryLight/40 px-3 py-2 text-xs text-garden-ink">
          <p className="flex-1 min-w-0 leading-snug">{composerHint}</p>
          <button
            type="button"
            onClick={() => setComposerHint(null)}
            className="shrink-0 min-h-8 min-w-8 rounded-full text-garden-muted hover:bg-black/5"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      ) : null}

      <div
        className="border-t border-black/5 bg-white/95 backdrop-blur px-3 pt-2"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
      >
        {imagePreview ? (
          <div className="mb-2 relative inline-block">
            <img
              src={imagePreview}
              alt=""
              className="h-16 w-16 object-cover rounded-lg border border-garden-ink/10"
            />
            <button
              onClick={() => setImage(null)}
              aria-label="Remove photo"
              className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center"
            >
              <X size={12} />
            </button>
          </div>
        ) : null}
        <div className="flex items-end gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            aria-label="Attach photo"
            className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted disabled:opacity-50"
          >
            <Camera size={22} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,image/heic,image/heif,.heic,.heif"
            capture="environment"
            onChange={onPickImage}
            className="hidden"
          />
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={input.length > 80 ? 4 : 2}
            placeholder="Ask about your garden…"
            className={
              'flex-1 resize-none px-3 py-2 rounded-2xl bg-white border border-garden-ink/10 text-sm focus:outline-none focus:border-garden-primary max-h-40 min-h-11 ' +
              (input.length > 80 ? 'min-h-[6.5rem]' : '')
            }
          />
          <button
            onClick={send}
            disabled={!canSend}
            aria-label="Send"
            className="min-h-11 min-w-11 rounded-full flex items-center justify-center bg-garden-primary text-white disabled:opacity-40 active:scale-95 transition"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  return (
    <li className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={
          'max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ' +
          (isUser
            ? 'bg-garden-primary text-white'
            : 'bg-white border border-garden-ink/10 text-garden-ink')
        }
      >
        {message.imageUrl ? (
          <img
            src={message.imageUrl}
            alt=""
            className="mb-1.5 max-h-40 rounded-lg object-cover"
          />
        ) : null}
        {message.text}
      </div>
    </li>
  );
}
